import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Shield, FileText, Save, Sparkles, Loader2, RotateCcw, Eye, ExternalLink } from 'lucide-react';
import AiGenerateButton from '@/components/ai/AiGenerateButton';

const SLUGS = [
  { slug: 'privacy', label: 'Privacy Policy', icon: Shield },
  { slug: 'terms',   label: 'Terms of Service', icon: FileText },
];

export default function LegalEditor() {
  const [active, setActive] = useState('privacy');
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const load = async (slug) => {
    setLoading(true);
    try {
      const r = await api.get(`/legal/${slug}`);
      setDoc(r.data);
    } catch (e) {
      toast.error('Failed to load page.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(active); }, [active]);

  const save = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const r = await api.put(`/admin/legal/${active}`, { title: doc.title, body: doc.body });
      setDoc(r.data);
      toast.success(`${active === 'privacy' ? 'Privacy Policy' : 'Terms of Service'} saved.`);
    } catch (e) {
      toast.error('Save failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally { setSaving(false); }
  };

  const regenerate = async () => {
    if (!window.confirm(`Replace the current ${active === 'privacy' ? 'Privacy Policy' : 'Terms of Service'} with a fresh AI-generated draft? Your edits will be lost.`)) return;
    setRegenerating(true);
    try {
      const r = await api.post(`/admin/legal/${active}/regenerate`);
      setDoc(r.data);
      toast.success('Fresh draft generated.');
    } catch (e) {
      toast.error('Regenerate failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally { setRegenerating(false); }
  };

  return (
    <div data-testid="legal-editor">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="flex gap-1">
          {SLUGS.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.slug}
                onClick={() => setActive(s.slug)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold rounded-full whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${active === s.slug ? 'bg-ink text-white' : 'text-muted-kc hover:bg-surface-2 border border-kc'}`}
                data-testid={`legal-tab-${s.slug}`}
              >
                <Icon size={12}/> {s.label}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <a href={`/legal/${active}`} target="_blank" rel="noreferrer" className="h-9 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] border border-kc text-sm hover:bg-surface-2" data-testid="legal-preview"><Eye size={14}/> View live <ExternalLink size={11}/></a>
          <button onClick={regenerate} disabled={regenerating} className="h-9 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] border border-[color:var(--kc-gold)] text-ink bg-gold-soft text-sm font-semibold disabled:opacity-50" data-testid="legal-regenerate">
            {regenerating ? <><Loader2 size={14} className="animate-spin"/> Regenerating…</> : <><Sparkles size={14} className="text-[color:var(--kc-gold-2)]"/> Regenerate with AI</>}
          </button>
        </div>
      </div>

      {loading || !doc ? (
        <div className="py-12 text-center text-muted-kc" data-testid="legal-editor-loading"><Loader2 className="inline animate-spin mr-2" size={16}/> Loading…</div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="block text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Page title</label>
              <AiGenerateButton
                page="legal"
                field={`${active}_title`}
                currentValue={doc.title || ''}
                onApply={(text) => setDoc({ ...doc, title: text })}
                length="short"
              />
            </div>
            <input
              type="text"
              value={doc.title || ''}
              onChange={(e) => setDoc({ ...doc, title: e.target.value })}
              className="w-full h-11 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white font-display text-xl focus:outline-none focus:border-[color:var(--kc-gold)]"
              data-testid="legal-title"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Page body — Markdown</label>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-muted-kc">
                  {doc.ai_generated ? 'Last AI-generated · ' : 'Custom · '}
                  {doc.updated_at ? new Date(doc.updated_at).toLocaleString() : '—'}
                </span>
                <AiGenerateButton
                  page="legal"
                  field={`${active}_body`}
                  currentValue={doc.body || ''}
                  onApply={(text) => setDoc({ ...doc, body: text })}
                  length="long"
                  label="AI rewrite body"
                />
              </div>
            </div>
            <textarea
              value={doc.body || ''}
              onChange={(e) => setDoc({ ...doc, body: e.target.value })}
              className="w-full px-3 py-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white font-mono text-sm leading-relaxed focus:outline-none focus:border-[color:var(--kc-gold)]"
              rows={28}
              data-testid="legal-body"
            />
            <div className="text-xs text-muted-kc mt-1">Supports Markdown — <code>## Heading</code>, <code>**bold**</code>, lists, links.</div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => load(active)} className="h-10 px-4 rounded-[var(--kc-radius-sm)] text-sm text-muted-kc hover:bg-surface-2 border border-kc inline-flex items-center gap-1" data-testid="legal-revert"><RotateCcw size={14}/> Revert</button>
            <button onClick={save} disabled={saving} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold disabled:opacity-50" data-testid="legal-save">
              {saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} Save changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
